import { useSharedColumnBuilder } from "hooks/useSharedColumnBuilder";

type Props = {
  viewOnly: boolean;
  editMode: boolean;
  fixture?: any;
  checkIfRecordBeingBusy?: (record: any) => boolean;
  updateRecord?: (data: any, record: any) => void;
  addRecord?: (record: any) => void;
  deleteRecord?: (record: any) => void;
};

export const useFixtureRosterColumns = ({
  viewOnly,
  editMode,
  checkIfRecordBeingBusy,
  updateRecord,
  addRecord,
  deleteRecord,
  fixture,
}: Props) => {
  const {
    createRosterNumberColumn,
    createRosterNameColumn,
    createPersonDobColumn,
    createPersonNationalityColumn,
    createPersonStatusColumn,
    createRosterPositionColumn,
    createRosterStatusColumn,
    createRosterIsStarterColumn,
    createRosterIsCaptainColumn,
    createPersonIsActiveColumn,
  } = useSharedColumnBuilder();

  const isPlayerNumberEditable = fixture?.isPlayerNumberEditable ?? false;
  const allowedPlayerSeasonRosterStatuses = fixture?.allowedPlayerSeasonRosterStatuses;

  const checkIfReadonly = () => !editMode || viewOnly;
  const checkIfReadonlyForNumber = () => checkIfReadonly() || !isPlayerNumberEditable;
  const checkIfDisabled = (record: any) =>
    (!record.isActive &&
      allowedPlayerSeasonRosterStatuses != null &&
      !allowedPlayerSeasonRosterStatuses.includes(record.seasonRosterPlayerStatus)) ||
    (checkIfRecordBeingBusy?.(record) ?? false);
  const includeIsActiveColumn = addRecord && deleteRecord;
  const addOrUpdateFunc = (data: any, record: any) => {
    Object.assign(record, data);
    if (record.isActive) {
      updateRecord?.(data, record);
    } else {
      record.isActive = true;
      addRecord?.(record);
    }
  };

  return [
    createRosterNumberColumn({
      readonly: checkIfReadonlyForNumber,
      disabled: checkIfDisabled,
      onChange: (value, record) => addOrUpdateFunc({ number: value }, record),
      withSorter: true,
    }),
    createRosterNameColumn({ withSorter: true }),
    createPersonDobColumn({ withSorter: true }),
    createPersonNationalityColumn(),
    createPersonStatusColumn(),
    createRosterPositionColumn({
      readonly: checkIfReadonly,
      disabled: checkIfDisabled,
      onChange: (value, record) => addOrUpdateFunc({ position: value }, record),
    }),
    createRosterStatusColumn(),
    includeIsActiveColumn
      ? createPersonIsActiveColumn({
          readonly: () => viewOnly,
          disabled: checkIfDisabled,
          onChange: (value, record) => {
            record.isActive = value;
            if (record.isActive) {
              addRecord?.(record);
            } else {
              deleteRecord?.(record);
            }
          },
        })
      : {},
    createRosterIsStarterColumn({
      readonly: checkIfReadonly,
      disabled: checkIfDisabled,
      onChange: (value, record) => addOrUpdateFunc({ isStarter: value }, record),
    }),
    createRosterIsCaptainColumn({
      readonly: checkIfReadonly,
      disabled: checkIfDisabled,
      onChange: (value, record) => addOrUpdateFunc({ isCaptain: value }, record),
    }),
  ];
};
